/**
 * /blog/[slug] - blog article detail (SSG-aware).
 *
 * Renders markdown body (HTML, sanitized) + injects JSON-LD blocks (Article,
 * BreadcrumbList) into <head>. Breadcrumb nav, author byline, last-updated
 * footer, related-posts strip (same cluster, top 3).
 *
 * SEO (build-time prerendered):
 *   - title `{post.title} - Asklaw`
 *   - meta description from frontmatter
 *   - canonical https://asklaw.vn/blog/[slug]
 *   - OG image from frontmatter (fallback /og/universal.png)
 *   - JSON-LD scripts (Article + BreadcrumbList + Speakable) trong <head>
 *
 * Tat ca metadata day vao <Head> tu vite-react-ssg (proxy
 * react-helmet-async). React-helmet-async serialize trong server render
 * pass + hydrate client. Khong con dung useEffect document.title patch
 * (KHONG hoat dong cho SSG vi crawler khong chay JS).
 *
 * getBlogStaticPaths: list 36 article slug, dung tai build-time de SSG
 * generate `dist/blog/<slug>/index.html`.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Head } from 'vite-react-ssg';
import {
  loadAllPostsSync,
  loadAllPosts,
  loadPostBySlug,
  loadPostBySlugSync,
  clusterLabel,
  buildHreflangLinks,
  type BlogPost,
} from '../lib/blog-loader';
import { NotFoundRoute } from './not-found';
import { trackEvent } from '../lib/analytics';

const SITE_ORIGIN = 'https://asklaw.vn';

export function BlogDetailRoute() {
  const params = useParams();
  const slug = params.slug ?? params['*'];

  // Sync-resolve khi co the (SSG pass + client first paint) de SSR-friendly:
  // build-time render co full content + meta tags. useState fallback de
  // tranh blank flicker khi slug doi (SPA navigation).
  const initialPost = useMemo(() => (slug ? loadPostBySlugSync(slug) : null), [slug]);
  const [post, setPost] = useState<BlogPost | null>(initialPost);
  const [related, setRelated] = useState<BlogPost[]>(() => {
    if (!initialPost) return [];
    return loadAllPostsSync()
      .filter((p) => p.cluster === initialPost.cluster && p.slugSegment !== initialPost.slugSegment)
      .slice(0, 3);
  });
  const [status, setStatus] = useState<'loading' | 'found' | 'not_found'>(
    initialPost ? 'found' : slug ? 'loading' : 'not_found',
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [slug]);

  // GA4 key event: engaged read (>= 25s dwell), once per article. PDPA-gated.
  useEffect(() => {
    if (!post) return;
    const id = window.setTimeout(
      () => trackEvent('blog_read', { slug: post.slugSegment, cluster: post.cluster }),
      25000,
    );
    return () => window.clearTimeout(id);
  }, [post?.slugSegment]);

  useEffect(() => {
    if (!slug) {
      setStatus('not_found');
      return;
    }
    // Neu sync resolve da work, skip async fetch.
    if (initialPost) {
      setPost(initialPost);
      setStatus('found');
      return;
    }
    let cancelled = false;
    (async () => {
      const found = await loadPostBySlug(slug);
      if (cancelled) return;
      if (!found) {
        setStatus('not_found');
        return;
      }
      setPost(found);
      setStatus('found');
      const all = await loadAllPosts();
      if (cancelled) return;
      setRelated(
        all
          .filter((p) => p.cluster === found.cluster && p.slugSegment !== found.slugSegment)
          .slice(0, 3),
      );
    })().catch((err: Error) => {
      // eslint-disable-next-line no-console
      console.error(
        JSON.stringify({
          level: 'error',
          scope: 'blog-detail',
          event: 'load_fail',
          slug,
          message: err.message,
        }),
      );
      if (!cancelled) setStatus('not_found');
    });
    return () => {
      cancelled = true;
    };
  }, [slug, initialPost]);

  // Canonical CO trailing slash de match URL serve thuc cua vite-react-ssg
  // dirStyle=nested + CF Pages routing (308 redirect khong-slash -> co-slash).
  // Tranh canonical/serve URL mismatch khien Google split signal.
  const canonical = useMemo(
    () => (post ? `${SITE_ORIGIN}/blog/${post.slugSegment}/` : null),
    [post],
  );

  if (status === 'loading') {
    return (
      <main style={{ maxWidth: 720, margin: '0 auto', padding: '40px 16px' }}>
        <p style={{ color: 'var(--asklaw-muted)' }}>Đang tải bài viết...</p>
      </main>
    );
  }
  if (status === 'not_found' || !post || !canonical) {
    return <NotFoundRoute />;
  }

  const ogImage = post.og_image
    ? `${SITE_ORIGIN}${post.og_image}`
    : `${SITE_ORIGIN}/og/universal.png`;

  // Auto-build BreadcrumbList JSON-LD tu nav structure cho moi article.
  // Nhieu article cu KHONG embed BreadcrumbList trong md - fix universal
  // de Google SERP hien breadcrumb sitelink + AEO chunk hieu cluster.
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: post.lang === 'en' ? 'Home' : 'Trang chủ',
        item: `${SITE_ORIGIN}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: `${SITE_ORIGIN}/blog`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: clusterLabel(post.cluster),
        item: `${SITE_ORIGIN}/blog`,
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: post.title,
        item: canonical,
      },
    ],
  };

  // Article (BlogPosting) JSON-LD built from frontmatter. The previous @graph
  // relied on `...post.jsonLd` (schema blocks extracted from the markdown body),
  // which most articles lack -> author/datePublished/dateModified never reached
  // Google as structured data (SEO-ROOTCAUSE-FIX 2026-06-10 root cause #5). Build
  // it here so all 36 articles emit a real Article node. Author is an
  // Organization until a named licensed lawyer reviewer is engaged (E-E-A-T
  // founder action, root cause #4); then switch to Person + add `reviewedBy`.
  const articleJsonLd = {
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description,
    inLanguage: post.lang ?? 'vi',
    datePublished: post.publish_date,
    dateModified: post.updated_date ?? post.publish_date,
    author: { '@type': 'Organization', name: post.author, url: `${SITE_ORIGIN}/about` },
    publisher: {
      '@type': 'Organization',
      name: 'Asklaw',
      logo: { '@type': 'ImageObject', url: `${SITE_ORIGIN}/logo-horizontal.png` },
    },
    image: ogImage,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
    url: canonical,
  };
  // Drop any Article/BlogPosting/NewsArticle already present in the markdown
  // jsonLd blocks to avoid emitting two Article nodes.
  const extraJsonLd = (post.jsonLd ?? []).filter((n) => {
    const t = (n as { '@type'?: string } | null)?.['@type'];
    return t !== 'Article' && t !== 'BlogPosting' && t !== 'NewsArticle';
  });

  // rel=alternate hreflang set (B-GAP-05). Empty for single-language articles;
  // self-referencing vi/en/x-default set when a cross-language counterpart
  // exists via frontmatter hreflang_en (on VI page) or hreflang_vi (on EN page).
  const hreflangLinks = buildHreflangLinks(post);

  return (
    <>
      <Head>
        {/*
          Dynamic <html lang> per article. Root cause Google Search Console
          classify EN article la "Soft 404" -> reject Request Indexing:
          `<html lang="vi">` hardcoded trong index.html nhung body tieng Anh ->
          lang/content mismatch. React-helmet-async support set html attribute
          qua `<html>` child element trong <Helmet>/<Head>. Fallback 'vi' giu
          default cho article khong khai bao lang (backward compat).
        */}
        <html lang={post.lang ?? 'vi'} />
        <title>{`${post.title} - Asklaw`}</title>
        <meta name="description" content={post.description} />
        <link rel="canonical" href={canonical} />
        {hreflangLinks.map((l) => (
          <link key={l.hreflang} rel="alternate" hrefLang={l.hreflang} href={l.href} />
        ))}
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={ogImage} />
        <meta property="article:published_time" content={post.publish_date} />
        {post.updated_date ? (
          <meta property="article:modified_time" content={post.updated_date} />
        ) : null}
        <meta property="article:author" content={post.author} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.description} />
        <meta name="twitter:image" content={ogImage} />
        {/*
          react-helmet-async dedup `<script type="application/ld+json">`
          neu render multiple tag - chi giu cai cuoi. Workaround: gom tat ca
          schema object vao 1 graph array trong single script. Google +
          Bing + Yandex deu support @graph composition.
        */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              articleJsonLd,
              ...extraJsonLd,
              breadcrumbJsonLd,
              {
                '@type': 'WebPage',
                speakable: {
                  '@type': 'SpeakableSpecification',
                  cssSelector: [
                    'article h1',
                    'article > p:first-of-type',
                    'article blockquote',
                  ],
                },
              },
            ],
          })}
        </script>
      </Head>
      <main style={{ maxWidth: 760, margin: '0 auto', padding: '24px 16px 96px' }}>
        <nav
          aria-label="Breadcrumb"
          style={{ fontSize: 13, color: 'var(--asklaw-muted)', marginBottom: 16 }}
        >
          <Link to="/" style={{ color: 'inherit' }}>Trang chủ</Link>
          <span style={{ margin: '0 6px' }}>/</span>
          <Link to="/blog" style={{ color: 'inherit' }}>Blog</Link>
          <span style={{ margin: '0 6px' }}>/</span>
          <span style={{ color: 'var(--asklaw-ink)' }}>{clusterLabel(post.cluster)}</span>
        </nav>

        <article>
          <header style={{ marginBottom: 24 }}>
            <span
              style={{
                display: 'inline-block',
                fontSize: 12,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'var(--asklaw-orange)',
                marginBottom: 8,
              }}
            >
              {clusterLabel(post.cluster)}
            </span>
            <h1
              className="h-display"
              style={{
                fontSize: 'clamp(28px, 4.5vw, 40px)',
                lineHeight: 1.2,
                margin: '0 0 12px 0',
              }}
            >
              {post.title}
            </h1>
            <p
              style={{
                fontSize: 17,
                lineHeight: 1.5,
                color: 'var(--asklaw-muted)',
                margin: '0 0 16px 0',
              }}
            >
              {post.description}
            </p>
            <div
              style={{
                display: 'flex',
                gap: 12,
                flexWrap: 'wrap',
                fontSize: 13,
                color: 'var(--asklaw-muted)',
                alignItems: 'center',
              }}
            >
              <Link to="/about#editorial-team" style={{ color: 'inherit', fontWeight: 600 }}>
                {post.author}
              </Link>
              <span aria-hidden>|</span>
              <span>{formatDate(post.publish_date)}</span>
              <span aria-hidden>|</span>
              <span>{post.readMinutes} phút đọc</span>
              {post.citations_count ? (
                <>
                  <span aria-hidden>|</span>
                  <span>{post.citations_count} trích dẫn pháp lý</span>
                </>
              ) : null}
            </div>
          </header>

          <div
            className="blog-body"
            // Body is sanitized by DOMPurify in blog-loader (client),
            // raw marked HTML during SSG build (no DOM available); both safe
            // vi nguon markdown la trusted editorial content (KHONG user input).
            dangerouslySetInnerHTML={{ __html: post.bodyHtml }}
            style={{
              fontSize: 16,
              lineHeight: 1.7,
              color: 'var(--asklaw-ink)',
            }}
          />

          <footer
            style={{
              marginTop: 32,
              paddingTop: 16,
              borderTop: '1px solid var(--asklaw-border)',
              fontSize: 13,
              color: 'var(--asklaw-muted)',
            }}
          >
            {post.lang === 'en' ? (
              <>
                <p style={{ margin: '0 0 6px 0' }}>
                  Last updated: {formatDate(post.updated_date ?? post.publish_date)}.
                </p>
                <p style={{ margin: 0 }}>
                  General information, not legal advice for any specific case.{' '}
                  <Link to="/terms" style={{ color: 'inherit', textDecoration: 'underline' }}>
                    See terms.
                  </Link>
                </p>
              </>
            ) : (
              <>
                <p style={{ margin: '0 0 6px 0' }}>
                  Cập nhật lần cuối: {formatDate(post.updated_date ?? post.publish_date)}.
                </p>
                <p style={{ margin: 0 }}>
                  Nội dung mang tính thông tin chung, không phải tư vấn pháp lý cho trường hợp cụ thể.{' '}
                  <Link to="/terms" style={{ color: 'inherit', textDecoration: 'underline' }}>
                    Xem điều khoản.
                  </Link>
                </p>
              </>
            )}
          </footer>
        </article>

        {related.length > 0 ? (
          <section style={{ marginTop: 40 }} aria-labelledby="related-heading">
            <h2 id="related-heading" style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>
              {post.lang === 'en' ? 'Related articles' : 'Bài viết cùng chủ đề'}
            </h2>
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'grid',
                gap: 12,
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              }}
            >
              {related.map((r) => (
                <li key={r.slugSegment}>
                  <Link
                    to={`/blog/${r.slugSegment}/`}
                    style={{
                      display: 'block',
                      padding: 12,
                      border: '1px solid var(--asklaw-border)',
                      borderRadius: 10,
                      textDecoration: 'none',
                      color: 'inherit',
                      fontSize: 14,
                      fontWeight: 600,
                      lineHeight: 1.4,
                    }}
                  >
                    {r.title}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </main>
    </>
  );
}

function formatDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/**
 * getBlogStaticPaths - dung boi vite-react-ssg de enumerate route can prerender.
 *
 * Tra ve `blog/<slug>` (KHONG kem leading slash, per vite-react-ssg convention
 * cho dynamic route co `path: ':slug'` ben trong parent `path: 'blog'`).
 *
 * Source of truth: blog-loader.loadAllPostsSync() doc 36 markdown file qua
 * import.meta.glob (eager). Khong fetch network, isomorphic Node + browser.
 */
export function getBlogStaticPaths(): string[] {
  return loadAllPostsSync().map((p) => `blog/${p.slugSegment}`);
}
